import { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  LifecycleStatus,
  getLifecycleStatusLabel,
  requiresChurnConfirmation,
} from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

type UseLifecycleStatusActionsArgs = {
  tenantId: string;
  userId: string;
  currentStatus: LifecycleStatus;
  /** Called after a successful change (or a 409) so the list can refetch. */
  onChanged: () => void;
};

/**
 * Owns the "Set status" behavior shared by the Org Admin and Admin Dash Users
 * pages (ENG-4694): which manual transitions need a confirmation dialog, the
 * ts-rest write to the single lifecycle write-path (ENG-4693), and refetch on
 * success / concurrent-change. The owning component renders the submenu items
 * and the confirm dialog as siblings, wired to the returned state.
 */
export const useLifecycleStatusActions = ({
  tenantId,
  userId,
  currentStatus,
  onChanged,
}: UseLifecycleStatusActionsArgs) => {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const toast = useToast();
  const [confirmTarget, setConfirmTarget] = useState<LifecycleStatus | null>(
    null,
  );
  const [isPending, setIsPending] = useState(false);

  const applyStatus = async (status: LifecycleStatus) => {
    setIsPending(true);
    try {
      const response =
        await apiClient.tenantUserLifecycle.setUserLifecycleStatus({
          params: { tenantId, userId },
          body: { status, expectedStatus: currentStatus },
        });

      if (response.status === 200) {
        toast.success(
          t('smartBlocks.lifecycleStatus.updated', {
            status: getLifecycleStatusLabel(status),
          }),
        );
        onChanged();
      } else if (response.status === 409) {
        // Lost a concurrent write race — the row moved underneath us. Refetch
        // so the list reflects the current state.
        toast.info(t('smartBlocks.lifecycleStatus.changedElsewhere'));
        onChanged();
      } else {
        toast.error(t('smartBlocks.lifecycleStatus.updateFailed'));
      }
    } catch {
      toast.error(t('smartBlocks.lifecycleStatus.updateFailed'));
    } finally {
      setIsPending(false);
      setConfirmTarget(null);
    }
  };

  /** Menu-item handler: confirm churn directions, apply the rest immediately. */
  const selectTransition = (target: LifecycleStatus) => {
    if (target === currentStatus) {
      return;
    }
    if (requiresChurnConfirmation(currentStatus, target)) {
      setConfirmTarget(target);
    } else {
      void applyStatus(target);
    }
  };

  const confirm = () => {
    if (confirmTarget) {
      void applyStatus(confirmTarget);
    }
  };

  const cancel = () => setConfirmTarget(null);

  return {
    selectTransition,
    confirmTarget,
    isConfirmOpen: confirmTarget !== null,
    confirm,
    cancel,
    isPending,
  };
};
